import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Chip,
	CircularProgress,
	Container,
	Link,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteHmrcOauthRequest, getIntegrationsRequest } from 'redux/reducers/settings/reducer';
import { getIntegrations, getIntegrationsLoader } from 'redux/reducers/settings/selectors';
import { TIntegrationItem } from 'redux/reducers/settings/types';
import {
	syncOrderShopifyRequest,
	syncOrderShopwareRequest,
	syncOrderWixRequest,
} from 'redux/reducers/ordersSync/reducer';
import { notificationContainer } from 'utils/notificationContainer';
import {
	INTEGRATIONS_PLATFORMS_IMG,
	INTEGRATIONS_PLATFORMS_IMG_TEXT,
	INTEGRATIONS_PLATFORMS_NAMES,
	IntegrationsPlatform,
} from 'redux/reducers/integrations/constants';
import { NewIntegrationDialog } from './NewIntegrationDialog';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';

export const Integrations: FC = () => {
	const dispatch = useDispatch();
	const integrations = useSelector(getIntegrations)?.integrations;
	const integrationsLoader = useSelector(getIntegrationsLoader);

	const [openSyncOrdersDialog, setOpenSyncOrdersDialog] = useState(false);
	const [selectedIntegration, setSelectedIntegration] = useState<TIntegrationItem | null>(null);

	useEffect(() => {
		dispatch(getIntegrationsRequest());
	}, [dispatch]);

	const [openCreateIntegration, setOpenCreateIntegration] = useState(false);

	const handleCloseCreateIntegration = () => {
		setOpenCreateIntegration(false);
	};

	const withHttp = (url: string) => {
		return !/^https?:\/\//i.test(url) ? `https://${url}` : url;
	};

	const withoutHttp = (url: string) => {
		return url?.replace(/^https?:\/\//i, '');
	};

	const handleDelete = () => {
		dispatch(deleteHmrcOauthRequest());
	};

	const handleSyncOrders = () => {
		if (!selectedIntegration) return;
		switch (selectedIntegration.platform) {
			case 'shopify':
				dispatch(syncOrderShopifyRequest({ id: selectedIntegration.id }));
				break;
			case 'wix':
				dispatch(syncOrderWixRequest({ id: selectedIntegration.id }));
				break;
			case 'shopware':
				dispatch(syncOrderShopwareRequest({ id: selectedIntegration.id }));
				break;
		}
		setSelectedIntegration(null);
	};

	return (
		<Container sx={{ mb: '20px' }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: '20px',
				}}
			>
				<Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: 3, marginTop: 3 }}>
					Integrations
				</Typography>
				<Box display="flex" gap={1} sx={{ alignItems: 'center' }}>
					{integrationsLoader && <CircularProgress size={20} />}
					<Button
						variant="contained"
						color="primary"
						onClick={() => setOpenCreateIntegration(true)}
					>
						Add New Integration
					</Button>
				</Box>
			</Box>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
					gap: 3,
				}}
			>
				{!!integrations?.length &&
					integrations?.map((integration) => (
						<Card key={integration.id + '-' + integration.platform}>
							<CardContent>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
										gap: '6px',
									}}
								>
									<img
										style={{
											height: '35px',
											objectFit: 'contain',
											textAlign: 'left',
											maxWidth: '200px',
										}}
										src={
											integration.platform === IntegrationsPlatform.SHOPIFY
												? INTEGRATIONS_PLATFORMS_IMG[IntegrationsPlatform.SHOPIFY]
												: integration.platform === 'shopware'
													? INTEGRATIONS_PLATFORMS_IMG[IntegrationsPlatform.SHOPWARE]
													: integration.platform === 'wix'
														? INTEGRATIONS_PLATFORMS_IMG[IntegrationsPlatform.WIX]
														: integration.platform === 'wooCommerce'
															? INTEGRATIONS_PLATFORMS_IMG[IntegrationsPlatform.WOOCOMMERCE]
															: integration.platform === 'HMRC'
																? INTEGRATIONS_PLATFORMS_IMG[IntegrationsPlatform.HMRC]
																: integration.platform === 'squarespace'
																	? INTEGRATIONS_PLATFORMS_IMG[IntegrationsPlatform.SQUARESPACE]
																	: integration.platform === IntegrationsPlatform.PRESTASHOP
																		? INTEGRATIONS_PLATFORMS_IMG[IntegrationsPlatform.PRESTASHOP]
																		: integration.platform === IntegrationsPlatform.STRIPE
																			? INTEGRATIONS_PLATFORMS_IMG[IntegrationsPlatform.STRIPE]
																			: ''
										}
										alt={
											integration.platform === IntegrationsPlatform.SHOPIFY
												? INTEGRATIONS_PLATFORMS_NAMES[IntegrationsPlatform.SHOPIFY]
												: integration.platform === IntegrationsPlatform.SHOPWARE
													? INTEGRATIONS_PLATFORMS_NAMES[IntegrationsPlatform.SHOPWARE]
													: integration.platform === IntegrationsPlatform.WIX
														? INTEGRATIONS_PLATFORMS_NAMES[IntegrationsPlatform.WIX]
														: integration.platform === IntegrationsPlatform.WOOCOMMERCE
															? INTEGRATIONS_PLATFORMS_NAMES[IntegrationsPlatform.WOOCOMMERCE]
															: integration.platform === IntegrationsPlatform.HMRC
																? INTEGRATIONS_PLATFORMS_NAMES[IntegrationsPlatform.HMRC]
																: integration.platform === IntegrationsPlatform.SQUARESPACE
																	? INTEGRATIONS_PLATFORMS_NAMES[IntegrationsPlatform.SQUARESPACE]
																	: integration.platform === IntegrationsPlatform.STRIPE
																		? INTEGRATIONS_PLATFORMS_NAMES[IntegrationsPlatform.STRIPE]
																		: integration.platform
										}
									/>
									<Typography
										style={{
											fontSize: '1.2rem',
											fontWeight: 550,
										}}
									>
										{INTEGRATIONS_PLATFORMS_IMG_TEXT[integration.platform as IntegrationsPlatform]}
									</Typography>
								</Box>
								<TableContainer>
									<Table>
										<TableBody>
											<TableRow>
												<TableCell
													sx={{
														border: 'none',
														p: '5px',
													}}
												>
													{integration.platform === IntegrationsPlatform.STRIPE
														? 'Account Name:'
														: 'Store URL:'}
												</TableCell>
												<TableCell
													sx={{
														border: 'none',
														p: '5px',
													}}
												>
													{integration.platform === IntegrationsPlatform.STRIPE ? (
														<Typography
															sx={{
																fontWeight: 'bold',
																fontSize: '1rem',
															}}
														>
															{integration.name}
														</Typography>
													) : (
														<Link
															sx={{
																color: 'primary.main',
																textDecoration: 'none',
																'&:hover': {
																	textDecoration: 'underline',
																},
																fontWeight: 'bold',
																fontSize: '1rem',
															}}
															href={withHttp(integration.domain)}
															target="_blank"
															rel="noreferrer"
														>
															{withoutHttp(integration.domain)}
														</Link>
													)}
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell
													sx={{
														border: 'none',
														p: '5px',
													}}
												>
													Installed at:
												</TableCell>
												<TableCell
													sx={{
														fontSize: '1rem',
														fontWeight: 'bold',
														border: 'none',
														p: '5px',
													}}
												>
													{DateTime.fromISO(integration.createdAt).toFormat('yyyy-MM-dd')}
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell
													sx={{
														border: 'none',
														p: '5px',
													}}
												>
													Current Status:
												</TableCell>
												<TableCell
													sx={{
														fontSize: '1rem',
														fontWeight: 'bold',
														border: 'none',
														p: '5px',
													}}
												>
													<Chip
														label={integration.enable ? 'Enabled' : 'Disabled'}
														color={integration.enable ? 'success' : 'error'}
														size="small"
														sx={{
															backgroundColor: integration.enable ? 'success.light' : 'error.light',
															color: integration.enable ? 'success.dark' : 'error.dark',
														}}
													/>
													{/* {integration.enable ? 'Enabled' : 'Disabled'} */}
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell
													sx={{
														border: 'none',
														p: '5px',
													}}
												>
													Changed at:
												</TableCell>
												<TableCell
													sx={{
														fontSize: '1rem',
														fontWeight: 'bold',
														border: 'none',
														p: '5px',
													}}
												>
													{DateTime.fromISO(integration.updatedAt).toFormat('yyyy-MM-dd')}
												</TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</TableContainer>
							</CardContent>
							<CardActions sx={{ display: 'flex', justifyContent: 'end' }}>
								{integration.platform === 'HMRC' && (
									<Button
										variant="contained"
										color="primary"
										size="small"
										sx={{
											mt: '10px',
											textTransform: 'none',
										}}
										onClick={handleDelete}
									>
										Delete HMRC Integration
									</Button>
								)}

								{integration.enable &&
									['shopify', 'wix', 'shopware'].includes(integration.platform) && (
										<Button
											variant="contained"
											color="primary"
											size="small"
											sx={{
												mt: '10px',
												textTransform: 'none',
											}}
											onClick={() => {
												setSelectedIntegration(integration);
												setOpenSyncOrdersDialog(true);
											}}
										>
											Sync Orders
										</Button>
									)}
							</CardActions>
						</Card>
					))}
				{!integrationsLoader && !integrations?.length && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'start',
							alignItems: 'start',
							// height: '620px',
							width: '100%',
							color: '#016196',
							fontSize: '24px',
							fontWeight: 'bold',
							pt: '20px',
						}}
					>
						No integrations found...
					</Box>
				)}
				{integrationsLoader && !integrations?.length && (
					<>
						{[1, 2].map((item) => (
							<Skeleton
								key={item}
								variant="rectangular"
								height="213px"
								sx={{
									borderRadius: '12px',
								}}
							/>
						))}
					</>
				)}
			</Box>
			<NewIntegrationDialog open={openCreateIntegration} setClose={handleCloseCreateIntegration} />
			<ConfirmationDialog
				open={!!selectedIntegration}
				setClose={() => setSelectedIntegration(null)}
				handleConfirm={handleSyncOrders}
				title="Sync Orders"
				text="Are you sure you want to sync orders with the store?"
			/>
		</Container>
	);
};
